import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { FUNCTIONAL_ASSESSMENTS } from "../common/enums";

// ---------------------------------------------------------------------------
// Start Page
// ---------------------------------------------------------------------------

const startSchema = z.object({
	impact: z.boolean().default(true).describe("Has Impact Button?"),
	info: z.boolean().default(true).describe("Has Info Button?"),
	isBackgroundChanged: z.boolean().default(false).describe("Does the Background Change Depending on the Language?"),
});

// ---------------------------------------------------------------------------
// Offer Page
// ---------------------------------------------------------------------------

const offerPageSchema = z.object({
	displayEstimatedPrice: z.boolean().default(true).describe("Display Estimated Price"),
});

// ---------------------------------------------------------------------------
// Trade-in Completion Page
// ---------------------------------------------------------------------------

const tradeInCompletionPageSchema = z.object({
	canFinaliseSession: z.boolean().default(false).describe("Complete Transaction in Web App"),
	customGradingReport: z.boolean().default(false).describe("Custom Grading Report"),
	allowFinaliseWithoutPin: z.boolean().default(false).describe("Allow Finalise Without Pin"),
	finalizeOnVision: z.boolean().default(false).describe("Finalize on Vision"),
});

// ---------------------------------------------------------------------------
// Redirection Options
// ---------------------------------------------------------------------------

const redirectionOptionsSchema = z.object({
	step: z
		.enum(["", "assessments", "add-assessments", "assessment-results", "imei", "offer"])
		// `""` is the enum's own "no redirection step" member, so the field is always
		// present rather than optional — consumers branch on the value, not its absence.
		.default("")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Step"),
	preRedirectionMessage: z.string().optional().describe("Pre-Redirection Message"),
});

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

const locationsSchema = z.object({
	includeRelated: z.boolean().default(true).describe("Include Related Partner Locations"),
});

// ---------------------------------------------------------------------------
// Webapp Schema
// ---------------------------------------------------------------------------

export const webappShape = {
	start: startSchema.prefault({}).describe("Start Page Options"),
	offerPage: offerPageSchema.prefault({}).describe("Offer Page Options"),
	tradeInCompletionPage: tradeInCompletionPageSchema.prefault({}).describe("Trade-in Completion Page Options"),
	redirection_options: redirectionOptionsSchema.prefault({}).describe("Redirection Options"),
	locations: locationsSchema.prefault({}).describe("Locations"),
	assessments: z
		.array(z.enum(FUNCTIONAL_ASSESSMENTS))
		.default(["digitizer", "audio", "multitouch", "biometrics", "gyroscope", "front-camera-ai", "back-camera-ai"])
		.check(
			fieldConfig({
				fieldType: "multiselect",
				description: "The supported assessments",
			}),
		)
		.describe("Assessments"),
	pages: z
		.array(z.enum(["start", "what-you-need", "assessment-results", "user-info", "offer", "store-locations"]))
		.default(["user-info", "offer"])
		.check(
			fieldConfig({
				fieldType: "multiselect",
				description: "The supported pages",
			}),
		)
		.describe("Pages"),
};

export const webappSchema = z.object(webappShape);

export type WebappConfig = z.infer<typeof webappSchema>;
